import { BackendApi } from '../../../../../api/api.ts';
import { extractApiErrorMessage } from '../../../../../lib/apiErrors.ts';
import { BackendAPITypes } from '../../../../../generated/blueprint.ts';
import {
    DropdownVATOptions,
    getVatPercentageForDropdownVAT,
    mapDropdownVATToVATCategory,
    mapVATCategoryToDropdownVAT,
} from '../../../../../types/enums.ts';
import { toCentsFromMoney } from '../../../../../lib/Money.ts';
import { DEFAULT_CURRENCY } from '../../../../../types/constants.ts';
import { CatalogItem, CatalogItemFormValues } from './types.ts';
import type {Unit, VATCategory} from '../../../../../generated/enums.ts';

type CatalogueItemResponse =
    BackendAPITypes.BACKENDPreHandleSyncApiV1MeBusinessCreateCatalogueItemPostResponseBody;

export function toBackendVat(option: DropdownVATOptions): {
    vat_category: VATCategory;
    vat_rate_percentage: number;
} {
    return {
        vat_category: mapDropdownVATToVATCategory(option),
        vat_rate_percentage: (getVatPercentageForDropdownVAT(option) ?? 0) * 100,
    };
}

export function fromBackendVat(vatCategory: string, vatRatePercentage: number): DropdownVATOptions {
    return mapVATCategoryToDropdownVAT(vatCategory as VATCategory, vatRatePercentage / 100);
}

export function mapCatalogueItemResponse(response: CatalogueItemResponse): CatalogItem {
    return {
        id: response.id,
        name: response.name,
        unit_price_cents: response.unit_price_net.signed_amount,
        currency: response.unit_price_net.currency ?? DEFAULT_CURRENCY,
        vat_option: fromBackendVat(response.vat_category, response.vat_rate_percentage),
        unit: response.unit as Unit,
        times_used: response.times_used,
        archived_at: response.archived_at,
        created_at: response.created_at,
        updated_at: response.updated_at,
    };
}

function toRequestFields(values: CatalogItemFormValues) {
    return {
        name: values.name.trim(),
        unit_price_net: {
            signed_amount: toCentsFromMoney(values.unit_price!),
            currency: values.unit_price!.currency || DEFAULT_CURRENCY,
        },
        unit: values.unit,
        ...toBackendVat(values.vat_option),
    };
}

export async function listCatalogItems(api: BackendApi): Promise<CatalogItem[]> {
    try {
        const response = await api.Me__business__getCatalogueItems({});
        return response.items.map(mapCatalogueItemResponse);
    } catch (error) {
        throw asCatalogError(error, 'Artikelen ophalen mislukt');
    }
}

export async function createCatalogItem(
    api: BackendApi,
    values: CatalogItemFormValues,
): Promise<CatalogItem> {
    try {
        return mapCatalogueItemResponse(
            await api.Me__business__createCatalogueItem(toRequestFields(values)),
        );
    } catch (error) {
        throw asCatalogError(error, 'Artikel aanmaken mislukt');
    }
}

export async function changeCatalogItem(
    api: BackendApi,
    itemId: string,
    values: CatalogItemFormValues,
): Promise<CatalogItem> {
    try {
        return mapCatalogueItemResponse(
            await api.Me__business__changeCatalogueItem({
                item_id: itemId,
                ...toRequestFields(values),
            }),
        );
    } catch (error) {
        throw asCatalogError(error, 'Artikel bijwerken mislukt');
    }
}

function asCatalogError(error: unknown, fallback: string): unknown {
    if (!(error instanceof Error) || error.name !== 'Non200ResponseError') return error;

    const detail = extractApiErrorMessage(error);
    if (detail) return new Error(detail);

    return new Error(error.message ? `${fallback} (${error.message})` : fallback);
}

